/**
 * Turn a Clerk error into something a person can act on.
 *
 * Clerk returns structured errors whose `longMessage` is written for end users
 * and is usually good. A few codes are not, or are phrased for a context we do
 * not have, so those are replaced. Anything unmapped still gets Clerk's own
 * wording rather than a generic "something went wrong", which tells nobody
 * anything.
 */
const BY_CODE: Record<string, string> = {
  form_identifier_not_found: 'No account with that email address.',
  form_password_incorrect: 'That password is not right.',
  strategy_for_user_invalid:
    'This account has no password set. Use a social provider, or ask for a sign-in code by email.',
  form_password_pwned:
    'That password has appeared in a known data breach. Please choose a different one.',
  form_password_length_too_short: 'That password is too short.',
  form_password_validation_failed: 'That password does not meet the requirements.',
  form_code_incorrect: 'That code is not right. Check it and try again.',
  verification_expired: 'That code has expired. Request a new one.',
  verification_failed: 'Too many incorrect attempts. Request a new code.',
  form_identifier_exists: 'That email address is already on another account.',
  session_exists: 'You are already signed in.',
  identifier_already_signed_in: 'You are already signed in.',
  external_account_exists: 'That account is already connected.',
  form_param_format_invalid: 'That does not look like a valid email address.',
  rate_limit_exceeded: 'Too many attempts. Wait a minute and try again.',
};

interface ClerkErrorShape {
  errors?: { code?: string; longMessage?: string; message?: string }[];
}

export function clerkError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const first = (err as ClerkErrorShape)?.errors?.[0];
  const mapped = first?.code ? BY_CODE[first.code] : undefined;
  if (mapped) return mapped;
  if (first?.longMessage) return first.longMessage;
  if (first?.message) return first.message;

  const message = (err as Error)?.message;
  // Clerk's network failures surface as bare TypeErrors, which say nothing
  // useful to whoever is looking at the screen.
  if (message && !/failed to fetch|networkerror|load failed/i.test(message)) return message;
  if (message) return 'Could not reach the sign-in service. Check your connection and try again.';

  return fallback;
}

/** Human label for an OAuth provider id such as `oauth_google`. */
export function providerLabel(provider: string): string {
  const clean = provider.replace(/^oauth_/, '');
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}
